import {Component, ElementRef, EventEmitter, Input, Output, SimpleChanges, ViewChild} from "@angular/core";
import {ResourceModel} from '../../../core/models/resources/resource.model';
import {Const, PolicyAction, ResourceState, ResourceType} from '../../../core/helpers/Const';
import {SwaService} from '../../../service/services/misc/swa/swa.service';
import {Utils} from '../../../core/helpers/Utils';
import {MenuComponent} from "@fundamental-ngx/core";
import {NotificationsService} from "../../../service/services/misc/notifications/notifications.service";
import {NavigationService} from "../../../service/services/misc/navigation/navigation.service";
import {ScrollStrategy} from "@angular/cdk/overlay";
import {ScrollDispatcher} from "@angular/cdk/scrolling";
import {BoundaryRepositionScrollStrategy} from "./boundary-reposition-scroll.strategy";

@Component({
    selector: 'app-organization-actions',
    templateUrl: './organization-actions.component.html',
    styleUrls: ['./organization-actions.component.scss'],
    standalone: false
})
export class OrganizationActionsComponent {
    Utils = Utils;
    @ViewChild(MenuComponent)
        menuComponent: MenuComponent;

    @Input() isFull = true;
    @Input() resource: ResourceModel;
    @Input() disabled: boolean;
    @Input() isFclClosed = false;
    @Input() busy = false;
    @Input() advancedSearchOpen = false;
    @Output() editOrganization: EventEmitter<ResourceModel> = new EventEmitter<ResourceModel>();
    @Output() editWithForm: EventEmitter<ResourceModel> = new EventEmitter<ResourceModel>();
    @Output() editWithYAML: EventEmitter<ResourceModel> = new EventEmitter<ResourceModel>();
	@Output() openDetails: EventEmitter<ResourceModel> = new EventEmitter<ResourceModel>();
    @Output() isOpen: EventEmitter<{ isOpen: boolean; item: ResourceModel }> = new EventEmitter<{
        isOpen: boolean;
        item: ResourceModel;
    }>();
    @Output() import: EventEmitter<ResourceModel> = new EventEmitter<ResourceModel>();
    @Output() delete: EventEmitter<ResourceModel> = new EventEmitter<ResourceModel>();
    @Output() openPolicies: EventEmitter<ResourceModel> = new EventEmitter<ResourceModel>();
    @Output() openReferences: EventEmitter<ResourceModel> = new EventEmitter<ResourceModel>();
    @Output() openEvents: EventEmitter<ResourceModel> = new EventEmitter<ResourceModel>();
    @Output() openRTDResources: EventEmitter<ResourceModel> = new EventEmitter<ResourceModel>();
    @Output() reloadResource: EventEmitter<ResourceModel> = new EventEmitter<ResourceModel>();
    @Output() reconcileResource: EventEmitter<ResourceModel> = new EventEmitter<ResourceModel>();
    @Output() moveResource: EventEmitter<ResourceModel> = new EventEmitter<ResourceModel>();
    @Output() analyzeFailure: EventEmitter<ResourceModel> = new EventEmitter<ResourceModel>();

    showEditableWithYaml = true;
    isDeleteButtonEnabled = true;
    isEditButtonEnabled = true;
    isEditWithYamlEnabled = true;
    isMoveResourceButtonEnabled = false;
    readonly scrollStrategy: ScrollStrategy;

    constructor(
        private swaService: SwaService,
        private notificationsService: NotificationsService,
        private navigationService: NavigationService,
        scrollDispatcher: ScrollDispatcher,
        elementRef: ElementRef
    ) {
        this.scrollStrategy = new BoundaryRepositionScrollStrategy(
            scrollDispatcher,
            () => elementRef?.nativeElement?.querySelector('.open-actions-button'),
            () => this.closeActionSheetComponent()
        );
    }

    actionPicked(action: string): void {
        this.swaService.sendCustomEvent(
            this.swaService.SWA_EVENT_TYPES.RESOURCE_ACTION_CLICKED,
            [action, this.resource.type],
            []
        );
        switch (action) {
        case 'open':
            this.openDetails.emit(this.resource);
            break;
        case 'edit':
            this.swaService.sendCustomEvent(this.swaService.SWA_EVENT_TYPES.EDIT_RESOURCE_WITH_YAML, [this.resource.type], []);
            this.editOrganization.emit(this.resource);
            break;
        case 'editWithForm':
            this.editWithForm.emit(this.resource);
            break;
        case 'import':
            this.import.emit(this.resource);
            break;
        case 'delete':
            this.delete.emit(this.resource);
            break;
        case 'move':
            this.moveResource.emit(this.resource);
            break;
        case 'policies':
            this.openPolicies.emit(this.resource);
            break;
        case 'references':
            this.openReferences.emit(this.resource);
            break;
        case 'events':
            this.openEvents.emit(this.resource);
            break;
        case 'showRTDResources':
            this.openRTDResources.emit(this.resource);
            break;
        case 'reload':
            this.reloadResource.emit(this.resource);
            break;
        case 'reconcile':
            this.reconcileResource.emit(this.resource);
            break;
        case 'analyzeFailure':
            this.analyzeFailure.emit(this.resource);
            break;
        default:
            // code block
        }
        this.closeActionSheetComponent();
    }

    ngOnChanges(changes: SimpleChanges) {
        this.showEditableWithYaml = this.showEditWithYaml(this.resource);
        this.isDeleteButtonEnabled = this.isDeleteEnabled(this.resource);
        this.isEditButtonEnabled = !this.isEditEnabled(this.resource);
        this.isEditWithYamlEnabled = !this.isEditEnabled(this.resource);
        this.isMoveResourceButtonEnabled = this.isMoveResourceEnabled(this.resource);
    }

    closeActionSheetComponent(): void {
        this.menuComponent.close();
    }

    setIsOpen(isOpen: boolean) {
        this.isOpen.emit({isOpen, item: this.resource});
    }

    isEditEnabled(resource: ResourceModel) {
        if (resource?.type === ResourceType.ORGANIZATION) {
            return true;
        }

        let rules = resource?.rules || [];

        return rules.some(r => r.action === PolicyAction.Update || r.action === PolicyAction.All);
    }

    isDeleteEnabled(resource: ResourceModel) {
        let rules = resource?.rules || [];

        return rules.some(r => r.action === PolicyAction.Delete || r.action === PolicyAction.All);
    }

    isMoveResourceEnabled(resource: ResourceModel) {
        let rules = resource?.rules || [];

        return Utils.hasFeatureFlag(Const.FEATURE_MOVE_RESOURCE) &&
            rules.some(r => r.action === PolicyAction.Move || r.action === PolicyAction.All);
    }

    showEditWithYaml(resource: ResourceModel) {
        return (
            resource !== undefined &&
            resource.type !== ResourceType.FOLDER &&
            resource.type !== ResourceType.RESOURCE_GROUP &&
            resource.type !== ResourceType.ORGANIZATION
        );
    }

    editResource(resource: ResourceModel) {
        if (
            resource.type === ResourceType.FOLDER ||
            resource.type === ResourceType.RESOURCE_GROUP ||
            resource.type === ResourceType.ORGANIZATION
        ) {
            this.actionPicked('edit');
        } else {
            this.actionPicked('editWithForm');
        }
    }

    onCopyPath() {
        this.notificationsService.showMessageToast($localize`:XMSG|Toast for copying path@@general.copyPathToast:Path copied to your clipboard`);
        Utils.copyToClipboard(this.resource.path);
        this.menuComponent.close();
    }

    async navigateToResource(): Promise<void> {
        if (this.resource.type === "Organization") {
            await this.navigationService.goToResource(Const.ALL_PROJECTS_PARAM, this.resource.name, undefined, undefined);
            this.closeActionSheetComponent();
            return;
        }

        // Parse the resource path to get organization and parent path segments
        const parts = this.resource.path.split("/").filter(segment => segment !== '');
        const organization = parts.shift(); // First part is organization name

        if (!parts.length) {
            // Resource is directly under organization
            await this.navigationService.goToResource(Const.ALL_PROJECTS_PARAM, organization, undefined, undefined);
            this.closeActionSheetComponent();
            return;
        }

        // Navigate to the parent container using navigateToResourceByPath
        // parts now contains the path segments between organization and the resource
        const parentName = parts.pop(); // Last part is the parent container name

        try {
            await this.navigationService.navigateToResourceByPath(
                parts,
                organization,
                parentName,
                () => this.closeActionSheetComponent()
            );
        } catch (error) {
            console.error('Navigation to parent failed:', error);
            // Close the menu even if navigation fails
            this.closeActionSheetComponent();
        }
    }

    get showAnalyzeFailure(): boolean {
        return Utils.isAiAssistantAvailable() && this.isErrorState(this.resource);
    }

    isErrorState(resource: ResourceModel): boolean {
        return resource?.state === ResourceState.FAILED
            || resource?.state === ResourceState.TERMINATION_FAILED
            || resource?.state === ResourceState.NOT_READY;
    }

    readonly featureToggles = Const;
    protected readonly ResourceType = ResourceType;
}
